import {
    Badge,
    Box,
    Flex,
    Heading,
    HStack,
    Icon,
    IconButton,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import { FiClock, FiDollarSign, FiPackage, FiPercent } from 'react-icons/fi';
import { TbCopy, TbExternalLink } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';
import OrderTag from '../OrderTag';
import { formatPrice } from '../../functions';
import { isEmbedMode } from '../../utils/embedMode';

/**
 * Info-graphic order header injected into Ask Carl responses. Mirrors the
 * OrderDetail page header (vehicle · RO heading, order tags, VIN/location/
 * insurance subline, KPI stat row) on the platform's light theme.
 */

export interface OrderContextSnapshot {
    orderId?: string;
    vehicleName?: string;
    roNumber?: string;
    vin?: string;
    customer?: string;
    claimNumber?: string;
    insurance?: string;
    locationName?: string;
    drpJob?: boolean;
    archived?: boolean;
    completed?: boolean;
    quotesOnly?: boolean;
    potentialTotal?: boolean;
    manuallyFlagged?: boolean;
    oemEndorsement?: boolean;
    oemCertification?: boolean;
    dealerReferral?: string;
    orderValue?: number;
    grossMargin?: number;
    partsOrdered?: number;
    totalParts?: number;
    deliveryTime?: string | null;
    deliveryETA?: string | null;
}

const StatChip = ({
    icon,
    label,
    value,
}: {
    icon: React.ElementType;
    label: string;
    value: string;
}) => (
    <Flex flex="1 1 150px" minW="150px" align="center" gap={2.5} bg="gray.50" py={2} px={3} borderRadius="lg" borderWidth="1px" borderColor="gray.200">
        <Flex align="center" justify="center" w="32px" h="32px" borderRadius="full" bg="blue.50" flexShrink={0}>
            <Icon as={icon} boxSize={4} color="#0071E3" />
        </Flex>
        <Box minW={0}>
            <Text fontSize="xs" fontWeight="medium" color="gray.500" whiteSpace="nowrap" lineHeight="1.2">{label}</Text>
            <Text fontSize="md" fontWeight="bold" color="gray.900" whiteSpace="nowrap" lineHeight="1.25">{value}</Text>
        </Box>
    </Flex>
);

const OrderContextCard = ({ order, onCopy }: { order: OrderContextSnapshot; onCopy?: (text: string, label: string) => void; }) => {
    const navigate = useNavigate();
    const location = useLocation();
    // Mobile WebView embed: the chat is the whole app surface — no jumping
    // out to the order detail page (App would bounce it back anyway).
    const isEmbedded = isEmbedMode(location.search);

    const marginText = typeof order.grossMargin === 'number' ? `${(order.grossMargin * 100).toFixed(1)}%` : '—';
    const orderValueText = typeof order.orderValue === 'number' ? formatPrice(order.orderValue) : '—';
    const partsText = typeof order.partsOrdered === 'number' && typeof order.totalParts === 'number'
        ? `${order.partsOrdered} / ${order.totalParts}`
        : '—';
    const deliveryText = order.deliveryETA || order.deliveryTime || 'N/A';
    const vin = order.vin ?? '';

    return (
        <Box border="1px solid" borderColor="gray.200" borderRadius="xl" bg="white" boxShadow="none" px={4} py={3.5}>
            <VStack align="stretch" spacing={3}>
                {/* Header row: vehicle · RO + tags, open-order link */}
                <Flex align="flex-start" justify="space-between" gap={2}>
                    <VStack spacing={1} align="flex-start" minW={0}>
                        <HStack spacing={2} align="center" flexWrap="wrap">
                            <Heading size="sm" color="gray.900" lineHeight="1.25" noOfLines={1}>
                                {order.vehicleName || 'Vehicle unavailable'}
                            </Heading>
                            <Heading size="sm" color="gray.400" lineHeight="1.25">·</Heading>
                            <Heading size="sm" color="gray.900" lineHeight="1.25">{order.roNumber || '—'}</Heading>
                            {order.archived && <OrderTag tag="archived" text="ARCHIVED" />}
                            {order.completed && <OrderTag tag="completed" text="COMPLETED" />}
                            {order.potentialTotal && <OrderTag tag="potential-total" text="Potential Total" />}
                            {order.oemCertification && <OrderTag tag="oem-cetification" text="OEM Certification" />}
                            {order.quotesOnly && <OrderTag tag="quotes-only" text="Quotes Only" />}
                            {order.oemEndorsement && <OrderTag tag="oem-endorsement" text="OEM Endorsement" />}
                            {order.dealerReferral && <OrderTag tag="dealer-referral" text={order.dealerReferral} />}
                            {order.manuallyFlagged && <OrderTag tag="manually-flagged" text="Save for Later" />}
                        </HStack>
                        <HStack spacing={1.5} align="center" flexWrap="wrap">
                            {vin && (
                                <HStack spacing={1} align="center">
                                    <Text fontSize="sm" color="gray.500" lineHeight="1.4">VIN:</Text>
                                    <Text as="span" fontSize="sm" color="gray.800" lineHeight="1.4">
                                        {vin.slice(0, -8)}
                                        <Text as="span" fontWeight="bold" textDecoration="underline">
                                            {vin.slice(-8)}
                                        </Text>
                                    </Text>
                                    {onCopy && (
                                        <IconButton
                                            aria-label="Copy full VIN"
                                            icon={<Icon as={TbCopy as React.ElementType} w={3} h={3} />}
                                            size="xs"
                                            variant="ghost"
                                            onClick={() => onCopy(vin, 'Full VIN')}
                                            p={0.5}
                                            minW="auto"
                                            h="auto"
                                            color="gray.400"
                                            _hover={{ color: 'gray.700' }}
                                        />
                                    )}
                                </HStack>
                            )}
                            {order.locationName && (
                                <>
                                    <Text fontSize="sm" color="gray.300" lineHeight="1">·</Text>
                                    <Text fontSize="sm" color="gray.500">Location: <Text as="span" color="gray.800">{order.locationName}</Text></Text>
                                </>
                            )}
                            <Text fontSize="sm" color="gray.300" lineHeight="1">·</Text>
                            <HStack spacing={1} align="center">
                                <Text fontSize="sm" color="gray.500">Insurance:</Text>
                                <Text fontSize="sm" color="gray.800">{order.insurance || '—'}</Text>
                                {order.drpJob && <Badge colorScheme="blue" variant="subtle" fontSize="10px">DRP</Badge>}
                            </HStack>
                            {order.customer && (
                                <>
                                    <Text fontSize="sm" color="gray.300" lineHeight="1">·</Text>
                                    <Text fontSize="sm" color="gray.500">Customer: <Text as="span" color="gray.800">{order.customer}</Text></Text>
                                </>
                            )}
                        </HStack>
                    </VStack>
                    {order.orderId && !isEmbedded && (
                        <Tooltip label="Open order" placement="top">
                            <IconButton
                                aria-label="Open order detail"
                                icon={<Icon as={TbExternalLink as React.ElementType} w={4} h={4} />}
                                size="sm"
                                variant="ghost"
                                color="#0071E3"
                                _hover={{ bg: 'blue.50' }}
                                onClick={() => navigate(`/order/${order.orderId}`)}
                                flexShrink={0}
                            />
                        </Tooltip>
                    )}
                </Flex>

                {/* KPI stat row */}
                <HStack spacing={3} flexWrap="wrap">
                    <StatChip icon={FiPackage as React.ElementType} label="Parts Ordered" value={partsText} />
                    <StatChip icon={FiDollarSign as React.ElementType} label="Order Value" value={orderValueText} />
                    <StatChip icon={FiPercent as React.ElementType} label="Gross Margin" value={marginText} />
                    <StatChip icon={FiClock as React.ElementType} label="Delivery" value={deliveryText} />
                </HStack>
            </VStack>
        </Box>
    );
};

export default OrderContextCard;
